// Single source of truth for signature placeholder chips — SignatureForm.jsx
// and UserDetailPopup.jsx both import from here instead of each keeping
// their own literal array (they used to be two separately-typed copies of
// the same list, a third being the backend's graph_utils.py dict keys,
// which still has to be kept in sync manually — see the comment there).
//
// Every token here is resolved server-side by the exact same {{Token}}
// mechanism (graph_utils.render_signature_html / computed_placeholders.py)
// — "personal" tokens come straight from the Microsoft Graph profile,
// "computed" tokens (photo/meeting link/vCard/brand logo) are resolved just before
// render. The chip's `html` is what actually gets inserted into the editor,
// so photo/vCard chips insert a ready-made <img>/<a> tag rather than a bare
// token the admin would have to wrap themselves. Disclaimer/BannerCampaign
// are NOT chips here (2026-09 restructure) — they're configured with their
// own content + target groups + position on the Yasal Uyarılar/Kampanyalar
// pages and injected automatically, not placed by hand in the editor.

export const PERSONAL_PLACEHOLDERS = [
  '{{GivenName}}',
  '{{Surname}}',
  '{{JobTitle}}',
  '{{StreetAddress}}',
  '{{TelephoneNumber}}',
  '{{Mail}}',
  '{{Mobile}}',
  '{{FacsimileTelephoneNumber}}',
];

// Human-readable labels for the PERSONAL_PLACEHOLDERS tokens — used by
// Settings > Placeholder (SettingsPage.js), where an admin edits the sample
// value each token substitutes to in SignatureForm.jsx's live preview.
// { tr, en } entries — resolve with i18n's pick().
export const PERSONAL_PLACEHOLDER_LABELS = {
  '{{GivenName}}': { tr: 'Ad', en: 'First name' },
  '{{Surname}}': { tr: 'Soyad', en: 'Last name' },
  '{{JobTitle}}': { tr: 'Unvan', en: 'Job title' },
  '{{StreetAddress}}': { tr: 'Adres', en: 'Address' },
  '{{TelephoneNumber}}': { tr: 'Telefon', en: 'Phone' },
  '{{Mail}}': { tr: 'E-posta', en: 'Email' },
  '{{Mobile}}': { tr: 'Cep Telefonu', en: 'Mobile' },
  '{{FacsimileTelephoneNumber}}': { tr: 'Faks', en: 'Fax' },
};

export const PERSONALIZATION_CHIPS = [
  {
    label: '{{ProfilePhoto}}',
    html: '<img src="{{ProfilePhoto}}" width="64" height="64" style="border-radius:50%;object-fit:cover;" alt="" />',
  },
  {
    label: '{{VCardQR}}',
    html: '<img src="{{VCardQR}}" width="80" height="80" alt="vCard QR kod" />',
  },
];

// {{MeetingLink}} was a chip here until 2026-09 and has since been removed
// entirely (Organization.meeting_link field, backend token, preview value):
// no UI ever existed to set it, so every org's link was permanently empty,
// and no template in production referenced the token (checked 2026-09-24).

// {{BrandLogo}} was a chip here until 2026-09, removed once SignatureForm.jsx
// got a real "Marka Kütüphanesinden Ekle" picker — inserting a SPECIFIC
// chosen logo's real URL is strictly more useful than a token that silently
// tracks "whichever logo is currently newest" and can't target one of
// several logo assets. The {{BrandLogo}} token itself is untouched
// server-side (computed_placeholders.py) so templates that already have it
// from before keep working — its PREVIEW_SAMPLE_VALUES entry below stays
// too, so re-opening one of those templates still previews correctly
// instead of showing a broken image icon.

// {{Survey}} is NOT a plain insert-on-click chip like the ones above — it
// needs stateful configuration (which rating scale), so SignatureForm.jsx
// gives it a dedicated control instead of a chip entry. This
// mirrors the backend's SurveyTemplate.SCALE_EMOJIS (backend/signatures/
// models.py) — same "kept in sync by hand" caveat as every token above:
// resolution happens in computed_placeholders.py's _resolve_survey_html,
// which must emit the exact same emoji/label per rating or the live
// preview here and the real rendered email will visibly disagree.
// Sample values substituted into SignatureForm.jsx's live preview only —
// never into the saved template HTML. Every token an admin can insert
// (PERSONAL_PLACEHOLDERS + the personalization ones below) gets a plausible
// stand-in so the preview always looks like a finished signature instead of
// showing raw {{Token}} text or a broken image icon for the two image
// tokens. {{Survey}} is deliberately NOT here — it needs the admin's chosen
// scale (3 vs 5 emojis), so SignatureForm.jsx substitutes it separately.
// Disclaimer/BannerCampaign are also not here — they're no longer
// insertable tokens, see the note above PERSONAL_PLACEHOLDERS.
const AVATAR_PLACEHOLDER_SVG = `<svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 64 64"><circle cx="32" cy="32" r="32" fill="#e5e7eb"/><circle cx="32" cy="24" r="12" fill="#9aa1bd"/><path d="M8 58c0-13 10-22 24-22s24 9 24 22" fill="#9aa1bd"/></svg>`;
const QR_PLACEHOLDER_SVG = `<svg xmlns="http://www.w3.org/2000/svg" width="80" height="80" viewBox="0 0 80 80"><rect width="80" height="80" fill="#fff"/><rect x="4" y="4" width="16" height="16" fill="#1a1a1a"/><rect x="9" y="9" width="6" height="6" fill="#fff"/><rect x="60" y="4" width="16" height="16" fill="#1a1a1a"/><rect x="65" y="9" width="6" height="6" fill="#fff"/><rect x="4" y="60" width="16" height="16" fill="#1a1a1a"/><rect x="9" y="65" width="6" height="6" fill="#fff"/><rect x="28" y="6" width="4" height="4" fill="#1a1a1a"/><rect x="36" y="10" width="4" height="4" fill="#1a1a1a"/><rect x="30" y="16" width="4" height="4" fill="#1a1a1a"/><rect x="6" y="28" width="4" height="4" fill="#1a1a1a"/><rect x="14" y="34" width="4" height="4" fill="#1a1a1a"/><rect x="28" y="28" width="6" height="6" fill="#1a1a1a"/><rect x="38" y="30" width="4" height="4" fill="#1a1a1a"/><rect x="46" y="26" width="4" height="4" fill="#1a1a1a"/><rect x="54" y="34" width="4" height="4" fill="#1a1a1a"/><rect x="30" y="42" width="4" height="4" fill="#1a1a1a"/><rect x="44" y="44" width="6" height="6" fill="#1a1a1a"/><rect x="60" y="40" width="4" height="4" fill="#1a1a1a"/><rect x="28" y="54" width="4" height="4" fill="#1a1a1a"/><rect x="40" y="60" width="4" height="4" fill="#1a1a1a"/><rect x="52" y="56" width="4" height="4" fill="#1a1a1a"/><rect x="60" y="64" width="4" height="4" fill="#1a1a1a"/></svg>`;
const BRAND_LOGO_PLACEHOLDER_SVG = `<svg xmlns="http://www.w3.org/2000/svg" width="160" height="48" viewBox="0 0 160 48"><rect width="160" height="48" rx="6" fill="#e5e7eb"/><text x="80" y="29" font-family="Arial, sans-serif" font-size="14" fill="#9aa1bd" text-anchor="middle">LOGO</text></svg>`;

const toDataUri = svg => `data:image/svg+xml,${encodeURIComponent(svg)}`;

export const PREVIEW_SAMPLE_VALUES = {
  '{{GivenName}}': 'Ahmet',
  '{{Surname}}': 'Yılmaz',
  '{{JobTitle}}': 'Satış Müdürü',
  '{{StreetAddress}}': 'Barbaros Mah. Al Sok. No:12, Ataşehir/İstanbul',
  '{{TelephoneNumber}}': '+90 212 555 01 23',
  '{{Mail}}': 'ahmet.yilmaz@sirket.com',
  '{{Mobile}}': '+90 532 555 01 23',
  '{{FacsimileTelephoneNumber}}': '+90 212 555 01 24',
  '{{ProfilePhoto}}': toDataUri(AVATAR_PLACEHOLDER_SVG),
  '{{VCardQR}}': toDataUri(QR_PLACEHOLDER_SVG),
  '{{BrandLogo}}': toDataUri(BRAND_LOGO_PLACEHOLDER_SVG),
};

export const SURVEY_SCALES = {
  3: {
    label: '3 Nokta',
    emojis: [
      { rating: 3, emoji: '😊', label: 'Memnun' },
      { rating: 2, emoji: '😐', label: 'Nötr' },
      { rating: 1, emoji: '😞', label: 'Memnun Değil' },
    ],
  },
  5: {
    label: '5 Nokta',
    emojis: [
      { rating: 5, emoji: '😍', label: 'Çok Memnun' },
      { rating: 4, emoji: '😊', label: 'Memnun' },
      { rating: 3, emoji: '😐', label: 'Nötr' },
      { rating: 2, emoji: '😕', label: 'Az Memnun' },
      { rating: 1, emoji: '😞', label: 'Memnun Değil' },
    ],
  },
};

// The survey styles an admin can pick: 'emoji' draws SURVEY_SCALES[scale]'s
// emojis; 'stars' draws `scale` gold ★ instead (the Nth star = rating N).
// Mirrors SurveyTemplate.style / emoji_scale() in backend/signatures/models.py.
// `label` is a { tr, en } entry (resolve with i18n's pick()).
export const SURVEY_OPTIONS = [
  { scale: 3, style: 'emoji', label: { tr: '3 Nokta', en: '3 Points' } },
  { scale: 5, style: 'emoji', label: { tr: '5 Nokta', en: '5 Points' } },
  { scale: 3, style: 'stars', label: { tr: '3 Yıldız', en: '3 Stars' } },
  { scale: 5, style: 'stars', label: { tr: '5 Yıldız', en: '5 Stars' } },
  { scale: 3, style: 'numbers', label: { tr: '3 Rakam', en: '3 Numbers' } },
  { scale: 5, style: 'numbers', label: { tr: '5 Rakam', en: '5 Numbers' } },
  { scale: 3, style: 'numbers_color', label: { tr: '3 Renkli Rakam', en: '3 Colored Numbers' } },
  { scale: 5, style: 'numbers_color', label: { tr: '5 Renkli Rakam', en: '5 Colored Numbers' } },
];

// English names of the SURVEY_SCALES rating labels, for the panel UI —
// same wording as SurveyTemplate's English labels (signatures/models.py).
const SURVEY_LABELS_EN = {
  'Çok Memnun': 'Very Satisfied', 'Memnun': 'Satisfied', 'Nötr': 'Neutral',
  'Az Memnun': 'Somewhat Dissatisfied', 'Memnun Değil': 'Not Satisfied',
};
export const surveyLabel = (label, lang) => (lang === 'en' && SURVEY_LABELS_EN[label]) || label;

export const isNumericSurveyStyle = style => style === 'numbers' || style === 'numbers_color';

// Mirrors SurveyTemplate.DEFAULT_QUESTIONS — shown when the admin leaves the question empty.
export const SURVEY_DEFAULT_QUESTIONS = {
  tr: 'Bizi değerlendirmek ister misiniz?',
  en: 'Would you like to rate us?',
  de: 'Möchten Sie uns bewerten?',
  fr: 'Souhaitez-vous nous évaluer ?',
  ro: 'Doriți să ne evaluați?',
  ru: 'Хотите оценить нас?',
  ar: 'هل تود تقييمنا؟',
};

const escapeHtml = text => String(text)
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;').replace(/'/g, '&#x27;');

// Mirrors SurveyTemplate.SCALE_END_LABELS / SCALE_COLORS (backend/signatures/models.py).
const SCALE_END_LABELS = {
  tr: ['Hiç memnun değilim', 'Çok memnunum'],
  en: ['Not satisfied at all', 'Very satisfied'],
  de: ['Überhaupt nicht zufrieden', 'Sehr zufrieden'],
  fr: ['Pas du tout satisfait', 'Très satisfait'],
  ro: ['Deloc mulțumit', 'Foarte mulțumit'],
  ru: ['Совсем не доволен', 'Очень доволен'],
  ar: ['غير راضٍ إطلاقًا', 'راضٍ جدًا'],
};
const SCALE_COLORS = {
  3: ['#D32F2F', '#FBC02D', '#388E3C'],
  5: ['#D32F2F', '#F57C00', '#FBC02D', '#7CB342', '#388E3C'],
};

const STAR = '\u2605';

// Rating entries best-to-worst, glyph = the emoji or that many ★ — same shape
// as the backend's emoji_scale().
export const surveyEntries = (scale, style = 'emoji') => SURVEY_SCALES[scale].emojis.map(e => ({
  rating: e.rating,
  label: e.label,
  emoji: style === 'stars' ? STAR.repeat(e.rating) : isNumericSurveyStyle(style) ? String(e.rating) : e.emoji,
}));

// The row as an email shows it: left to right, worst to best (rating 1..N) —
// for emojis too, so the best face is on the right like the Nth star.
const ascendingEntries = scale => [...SURVEY_SCALES[scale].emojis].sort((a, b) => a.rating - b.rating);

// Short text preview of a style's row ("😞 😐 😊", "★★★"), for the picker/badge.
export const surveyRowText = (scale, style = 'emoji') => {
  if (style === 'stars') return STAR.repeat(scale);
  if (isNumericSurveyStyle(style)) return `1–${scale}`;
  return ascendingEntries(scale).map(e => e.emoji).join(' ');
};

// 'numbers' / 'numbers_color' — same table markup as _render_numeric_survey
// (backend/signatures/computed_placeholders.py).
const numericSurveyMarkup = (scale, style, language = 'tr') => {
  const [low, high] = SCALE_END_LABELS[language] || SCALE_END_LABELS.tr;
  const font = 'font-family:Arial,Helvetica,sans-serif;';
  const cells = ascendingEntries(scale).map(({ rating, label }) => {
    const color = style === 'numbers_color' ? SCALE_COLORS[scale][rating - 1] : null;
    const text = '#1a1a1a';
    const box = color
      ? `background-color:${color};border:1px solid ${color};color:${text};`
      : `background-color:#ffffff;border:1px solid #B0B7C3;color:${text};`;
    return `<td width="44" align="center"${color ? ` bgcolor="${color}"` : ''} style="padding:0;">` +
      `<a href="#" title="${rating}/${scale} - ${label}" style="display:block;${box}border-radius:4px;padding:6px 0;` +
      `text-align:center;text-decoration:none;${font}font-size:15px;font-weight:bold;line-height:18px;">` +
      `<span style="color:${text};">${rating}</span></a></td>`;
  }).join('<td width="6" style="width:6px;padding:0;font-size:0;line-height:0;">&nbsp;</td>');
  const caption = `${font}font-size:11px;color:#5C6274;white-space:nowrap;`;
  return '<table role="presentation" cellpadding="0" cellspacing="0" border="0" style="border-collapse:collapse;">' +
    `<tr>${cells}</tr><tr><td colspan="${2 * scale - 1}" style="padding:4px 0 0 0;">` +
    '<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0"><tr>' +
    `<td align="left" style="${caption}">${low}</td><td align="right" style="${caption}padding-left:12px;">${high}</td>` +
    '</tr></table></td></tr></table>';
};

// Live-preview markup for {{Survey}} — must match _resolve_survey_html in
// backend/signatures/computed_placeholders.py. `question`: the admin's text
// ('' = the language default); leave it undefined to get just the rating row
// (the style picker's thumbnails).
export const surveyPreviewMarkup = (scale, style = 'emoji', language = 'tr', question) => {
  if (question === undefined) return surveyRowMarkup(scale, style, language);
  const text = (question || '').trim() || SURVEY_DEFAULT_QUESTIONS[language] || SURVEY_DEFAULT_QUESTIONS.tr;
  const questionHtml = '<div style="font-family:Arial,Helvetica,sans-serif;font-size:14px;line-height:1.4;' +
    `font-weight:normal;color:#1a1a1a;margin:0 0 4px 0;">${escapeHtml(text)}</div>`;
  const row = surveyRowMarkup(scale, style, language);
  return questionHtml + (isNumericSurveyStyle(style) ? row : `<div>${row}</div>`);
};

const surveyRowMarkup = (scale, style, language) => {
  if (isNumericSurveyStyle(style)) return numericSurveyMarkup(scale, style, language);
  if (style === 'stars') {
    return ascendingEntries(scale)
      .map(({ rating, label }) => (
        `<a href="#" style="text-decoration:none;font-size:26px;margin:0 1px;" title="${rating}/${scale} - ${label}">` +
        `<span style="color:#F59E0B;">${STAR}</span></a>`
      ))
      .join('');
  }
  return ascendingEntries(scale)
    .map(({ emoji, label }) => (
      `<a href="#" style="text-decoration:none;font-size:22px;margin:0 4px;" title="${label}">${emoji}</a>`
    ))
    .join('');
};
